import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AssignmentTurnedIn,
  CheckCircle,
  Close,
  CloudUpload,
  ContentCopy,
  DirectionsCar,
  ErrorOutline,
  InfoOutlined,
  LocationOn,
  Refresh,
  RemoveRedEye,
  Security,
  Sos,
  UploadFile,
  Warning,
  Delete,
} from "@mui/icons-material";

import CertificateService from "../../../services/certificateServices";


// =====================================================
// HELPERS
// =====================================================

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    "Something went wrong. Please try again."
  );
};


const getStatusColor = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "warning";

    case "assigned":
      return "info";

    case "submitted":
      return "success";

    case "verified":
      return "success";

    case "rejected":
      return "error";

    case "completed":
      return "success";

    default:
      return "default";
  }
};


const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

// =====================================================
// RELATIVE JOB TIMER
// =====================================================

const formatTimeAgo = (value, now = Date.now()) => {
  if (!value) return "-";

  const timestamp = new Date(value).getTime();

  if (Number.isNaN(timestamp)) return "-";

  const diffSeconds = Math.max(
    0,
    Math.floor((now - timestamp) / 1000)
  );

  if (diffSeconds < 60) {
    return `${diffSeconds} sec${diffSeconds === 1 ? "" : "s"} ago`;
  }

  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);

  if (diffDays < 30) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);

  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  }

  const diffYears = Math.floor(diffMonths / 12);

  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
};

// =====================================================
// MAIN COMPONENT
// =====================================================

const CertificateJobQueue = () => {

  const [activeTab, setActiveTab] = useState(0);

  const [pendingJobs, setPendingJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);

  const [loadingPending, setLoadingPending] = useState(false);
  const [loadingMine, setLoadingMine] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const [statusFilter, setStatusFilter] = useState("");

  const [selectedJob, setSelectedJob] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailSource, setDetailSource] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [sosDialog, setSosDialog] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [certificateLabel, setCertificateLabel] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const [sosAction, setSosAction] = useState("");

  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [activationLoading, setActivationLoading] = useState(false);
  const [rebootLoading, setRebootLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  // ===================================================
  // SNACKBAR
  // ===================================================

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };


  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  // ===================================================
  // LOAD PENDING JOBS
  // ===================================================

  const loadPendingJobs = useCallback(async () => {

    try {

      setLoadingPending(true);

      const response =
        await CertificateService.getCertificatePendingJobs();

      const results =
        response?.data?.results ||
        response?.data ||
        [];

      setPendingJobs(
        Array.isArray(results)
          ? results
          : []
      );

    } catch (error) {

      showMessage(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoadingPending(false);

    }

  }, []);


  // ===================================================
  // LOAD MY JOBS
  // ===================================================

  const loadMyJobs = useCallback(async () => {

    try {

      setLoadingMine(true);

      const params = {};

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response =
        await CertificateService.getMyCertificateJobs(
          params
        );

      const results =
        response?.data?.results ||
        response?.data ||
        [];

      const assignedJobs = Array.isArray(results)
        ? results.filter(
          (job) =>
            String(job?.status || "").toLowerCase() ===
            "assigned"
        )
        : [];

      setMyJobs(assignedJobs);

    } catch (error) {

      showMessage(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setLoadingMine(false);

    }

  }, [statusFilter]);


  // ===================================================
  // INITIAL LOAD
  // ===================================================

  useEffect(() => {
    loadPendingJobs();
    loadMyJobs();
  }, [loadPendingJobs, loadMyJobs]);

  // =====================================================
  // UPDATE RELATIVE JOB TIMER
  // =====================================================

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);
  // ===================================================
  // REFRESH
  // ===================================================

  const handleRefresh = async () => {

    setRefreshing(true);

    try {

      await Promise.all([
        loadPendingJobs(),
        loadMyJobs(),
      ]);

      showMessage(
        "Job queue refreshed successfully."
      );

    } finally {

      setRefreshing(false);

    }
  };


  // ===================================================
  // CLAIM JOB
  // ===================================================

  const handleClaimJob = async (job) => {

    if (!job?.id) {
      return;
    }

    try {

      setActionLoading(true);

      const response =
        await CertificateService.claimCertificateJob({
          job_id: job.id,
        });

      showMessage(
        response?.data?.message ||
        "Job claimed successfully."
      );

      await Promise.all([
        loadPendingJobs(),
        loadMyJobs(),
      ]);

    } catch (error) {

      showMessage(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setActionLoading(false);

    }
  };


  // ===================================================
  // JOB DETAIL
  // ===================================================

  const handleOpenDetail = async (job, isPending = false) => {
    try {
      setActionLoading(true);

      // Pending Queue:
      // Do NOT call the job/detail API.
      // The pending API response already contains the required data.
      if (isPending) {
        setSelectedJob(job);
        setDetailSource("pending");
        setDetailDialog(true);
        return;
      }

      // My Assigned Jobs:
      // Continue using the existing detail API.
      const response =
        await CertificateService.getCertificateJobDetail(job.id);

      setSelectedJob(response?.data || null);
      setDetailSource("mine");
      setDetailDialog(true);
    } catch (error) {
      showMessage(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };


  // ===================================================
  // UPLOAD
  // ===================================================

  // ===================================================
  // PROTOCOL FILE LIMITS
  // ===================================================

  const getProtocolFormat = () => {
    return String(
      selectedJob?.protocol_format ||
      selectedJob?.job?.protocol_format ||
      ""
    )
      .trim()
      .toLowerCase();
  };

  const getTotalCertificateFiles = () => {
    return jobFiles.length + pendingFiles.length;
  };

  const getMaxCertificateFiles = () => {
    const protocol = getProtocolFormat();

    if (protocol === "nicrajasthan") {
      return 2;
    }

    // niccommon = 1 or more, so no upper limit
    if (protocol === "niccommon") {
      return Infinity;
    }

    // Keep existing behaviour for unknown protocol
    return Infinity;
  };

  //   const handleUploadCertificate = async () => {
  //   if (!selectedFile) {
  //     showMessage(
  //       "Please select a certificate file.",
  //       "warning"
  //     );
  //     return;
  //   }

  //   const allowedTypes = [
  //     "application/pdf",
  //     "image/png",
  //     "image/jpeg",
  //     "application/vnd.ms-excel",
  //     "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   ];

  //   if (
  //     selectedFile.type &&
  //     !allowedTypes.includes(selectedFile.type)
  //   ) {
  //     showMessage(
  //       "Only PDF, PNG, JPG, XLS and XLSX files are allowed.",
  //       "error"
  //     );
  //     return;
  //   }

  //   const maxSize = 1024 * 1024;

  //   if (selectedFile.size > maxSize) {
  //     showMessage(
  //       "Certificate file size must not exceed 1 MB.",
  //       "error"
  //     );
  //     return;
  //   }

  //   // IMPORTANT:
  //   // Do NOT call upload API here.
  //   // Just store the file locally until Submit is clicked.

  //   setPendingFiles((prev) => [
  //     ...prev,
  //     {
  //       id: `${Date.now()}-${selectedFile.name}`,
  //       file: selectedFile,
  //       label: certificateLabel.trim(),
  //     },
  //   ]);

  //   setSelectedFile(null);
  //   setCertificateLabel("");
  //   setUploadDialog(false);

  //   showMessage(
  //     "Certificate added."
  //   );
  // };

  const handleUploadCertificate = async () => {
    if (!selectedFile) {
      showMessage(
        "Please select a certificate file.",
        "warning"
      );
      return;
    }

    const protocol = getProtocolFormat();
    const currentFileCount = getTotalCertificateFiles();
    const maxFiles = getMaxCertificateFiles();

    // ===================================================
    // NIC RAJASTHAN - MAX 2 FILES
    // ===================================================

    if (
      protocol === "nicrajasthan" &&
      currentFileCount >= maxFiles
    ) {
      showMessage(
        "NIC Rajasthan allows a maximum of 2 certificate files.",
        "warning"
      );
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (
      selectedFile.type &&
      !allowedTypes.includes(selectedFile.type)
    ) {
      showMessage(
        "Only PDF, PNG, JPG, XLS and XLSX files are allowed.",
        "error"
      );
      return;
    }

    const maxSize = 1024 * 1024;

    if (selectedFile.size > maxSize) {
      showMessage(
        "Certificate file size must not exceed 1 MB.",
        "error"
      );
      return;
    }

    // ===================================================
    // STORE FILE LOCALLY
    // API IS CALLED ONLY ON SUBMIT
    // ===================================================

    setPendingFiles((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${selectedFile.name}`,
        file: selectedFile,
        label: certificateLabel.trim(),
      },
    ]);

    setSelectedFile(null);
    setCertificateLabel("");
    setUploadDialog(false);

    showMessage("Certificate added.");
  };

  const handleRemovePendingFile = (fileId) => {
    setPendingFiles((prev) =>
      prev.filter((item) => item.id !== fileId)
    );

    showMessage("Certificate removed.", "success");
  };

  // ===================================================
  // SUBMIT JOB
  // ===================================================

  const handleSubmitJob = async () => {
    if (!selectedJob?.job?.id) {
      return;
    }

    const existingFiles =
      selectedJob?.job?.files ||
      selectedJob?.files ||
      [];

    const protocol = String(
      selectedJob?.protocol_format ||
      selectedJob?.job?.protocol_format ||
      ""
    )
      .trim()
      .toLowerCase();

    const totalFiles =
      existingFiles.length + pendingFiles.length;

    // NIC COMMON:
    // minimum 1 file, no maximum
    if (protocol === "niccommon" && totalFiles < 1) {
      showMessage(
        "NIC Common requires at least 1 certificate file.",
        "warning"
      );
      return;
    }

    // NIC RAJASTHAN:
    // maximum 2 files
    if (protocol === "nicrajasthan" && totalFiles > 2) {
      showMessage(
        "NIC Rajasthan allows a maximum of 2 certificate files.",
        "warning"
      );
      return;
    }

    // User can submit if there are already uploaded files
    // OR files waiting to be uploaded.
    if (!existingFiles.length && !pendingFiles.length) {
      showMessage(
        "Upload at least one certificate before submitting.",
        "warning"
      );
      return;
    }

    try {
      setActionLoading(true);

      /*
       * =====================================================
       * STEP 1: UPLOAD ALL PENDING FILES
       * =====================================================
       *
       * The upload API is called ONLY here,
       * when the user clicks Submit for Review.
       */

      if (pendingFiles.length > 0) {
        for (const pendingFile of pendingFiles) {
          const formData = new FormData();

          formData.append(
            "job_id",
            selectedJob.job.id
          );

          formData.append(
            "file",
            pendingFile.file
          );

          if (pendingFile.label?.trim()) {
            formData.append(
              "label",
              pendingFile.label.trim()
            );
          }

          await CertificateService.uploadCertificateJobFile(
            formData
          );
        }
      }

      /*
       * =====================================================
       * STEP 2: SUBMIT JOB
       * =====================================================
       */

      const response =
        await CertificateService.submitCertificateJob({
          job_id: selectedJob.job.id,
        });

      showMessage(
        response?.data?.message ||
        "Job submitted for review."
      );

      // Clear temporary files only after successful submission
      setPendingFiles([]);
      setSelectedFile(null);
      setCertificateLabel("");

      setDetailDialog(false);

      await Promise.all([
        loadPendingJobs(),
        loadMyJobs(),
      ]);

    } catch (error) {
      showMessage(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };


  // ===================================================
  // SOS
  // ===================================================

  const handleSOSAction = async () => {

    if (!selectedJob?.job?.id) {
      return;
    }

    try {

      setActionLoading(true);

      const payload = {
        job_id: selectedJob.job.id,
      };


      let response;


      if (sosAction === "start") {

        response =
          await CertificateService.startCertificateJobSOS(
            payload
          );

      } else if (sosAction === "start-confirm") {

        response =
          await CertificateService.confirmCertificateJobSOSStart(
            payload
          );

      } else if (sosAction === "stop") {

        response =
          await CertificateService.stopCertificateJobSOS(
            payload
          );

      } else if (sosAction === "stop-confirm") {

        response =
          await CertificateService.confirmCertificateJobSOSStop(
            payload
          );

      }


      showMessage(
        response?.data?.message ||
        "SOS operation completed."
      );

      setSosDialog(false);

    } catch (error) {

      showMessage(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setActionLoading(false);

    }
  };

  // ===================================================
  // ACTIVATION COMMAND
  // ===================================================

  const handleActivationCommand = async () => {
    if (!selectedJob?.job?.id) {
      showMessage("Job ID is missing.", "error");
      return;
    }

    if (!activationCode.trim()) {
      showMessage(
        "Please enter activation code.",
        "warning"
      );
      return;
    }

    try {
      setActivationLoading(true);

      const payload = {
        job_id: selectedJob.job.id,
        activation_code: activationCode,
      };

      const response =
        await CertificateService.activateCertificateJob(
          payload
        );

      const result = response?.data?.result;

      if (
        result === "delivered" ||
        result === "queued_no_ack"
      ) {
        showMessage(
          response?.data?.message ||
          "Activation command dispatched.",
          "success"
        );
      } else {
        showMessage(
          response?.data?.message ||
          "Failed to send activation command to device.",
          "error"
        );
      }

    } catch (error) {
      showMessage(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setActivationLoading(false);
    }
  };

  // ===================================================
  // REBOOT COMMAND
  // ===================================================

  const handleRebootCommand = async () => {
    if (!selectedJob?.job?.id) {
      showMessage("Job ID is missing.", "error");
      return;
    }

    try {
      setRebootLoading(true);

      const response =
        await CertificateService.rebootCertificateJob({
          job_id: selectedJob.job.id,
        });

      const result = response?.data?.result;

      if (
        result === "delivered" ||
        result === "queued_no_ack"
      ) {
        showMessage(
          response?.data?.message ||
          "Reboot command dispatched.",
          "success"
        );
      } else {
        showMessage(
          response?.data?.message ||
          "Failed to send reboot command to device.",
          "error"
        );
      }

    } catch (error) {
      showMessage(
        getErrorMessage(error),
        "error"
      );
    } finally {
      setRebootLoading(false);
    }
  };

  // ===================================================
  // TRACKING
  // ===================================================

  const handleTracking = async () => {

    if (!selectedJob?.job?.id) {
      return;
    }

    try {

      setTrackingLoading(true);

      const response =
        await CertificateService.getCertificateJobTracking(
          selectedJob.job.id
        );

      setTracking(
        response?.data || null
      );

    } catch (error) {

      showMessage(
        getErrorMessage(error),
        "error"
      );

    } finally {

      setTrackingLoading(false);

    }
  };


  // ===================================================
  // FILES
  // ===================================================

  const jobFiles = useMemo(() => {

    return (
      selectedJob?.job?.files ||
      selectedJob?.files ||
      []
    );

  }, [selectedJob]);


  // ===================================================
  // JOB TABLE
  // ===================================================

  const renderJobTable = (
    jobs,
    isPending
  ) => {

    if (
      (isPending && loadingPending) ||
      (!isPending && loadingMine)
    ) {

      return (
        <Box
          sx={{
            minHeight: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }


    if (!jobs.length) {

      return (
        <Box
          sx={{
            minHeight: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
          }}
        >

          <AssignmentTurnedIn
            sx={{
              fontSize: 48,
              color: "text.disabled",
            }}
          />

          <Typography
            variant="h6"
            color="text.secondary"
          >
            No jobs found
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            {isPending
              ? "There are no certificate jobs available in your scope."
              : "You do not have any assigned certificate jobs."}
          </Typography>

        </Box>
      );
    }


    return (
      <TableContainer>

        <Table>

          <TableHead>

            <TableRow>

              <TableCell>
                <strong>Job ID</strong>
              </TableCell>

              <TableCell>
                <strong>Vehicle</strong>
              </TableCell>

              <TableCell>
                <strong>IMEI</strong>
              </TableCell>

              <TableCell>
                <strong>Status</strong>
              </TableCell>

              <TableCell>
                <strong>Created</strong>
              </TableCell>

              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>

            </TableRow>

          </TableHead>


          <TableBody>

            {jobs.map((job) => (

              <TableRow
                key={job.id}
                hover
              >

                <TableCell>

                  <Typography
                    fontWeight={700}
                  >
                    {job.id}
                  </Typography>

                </TableCell>


                <TableCell>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >

                    <DirectionsCar
                      fontSize="small"
                      color="action"
                    />

                    <Box>

                      <Typography
                        variant="body2"
                        fontWeight={600}
                      >
                        {job.vehicle_reg_no ||
                          job.reg_no ||
                          job.vehicle_registration_number ||
                          "-"}
                      </Typography>

                      {/* <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {job.imei ||
                          "-"}
                      </Typography> */}

                    </Box>

                  </Stack>

                </TableCell>


                <TableCell>

                  {job.imei ||
                    "-"}

                </TableCell>


                <TableCell>

                  <Chip
                    size="small"
                    label={
                      job.status ||
                      "Unknown"
                    }
                    color={getStatusColor(
                      job.status
                    )}
                  />

                </TableCell>


                <TableCell>
                  <Tooltip
                    title={formatDate(
                      isPending ? job?.created : job?.assigned_at
                    )}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatTimeAgo(
                        isPending ? job?.created : job?.assigned_at
                      )}
                    </Typography>
                  </Tooltip>
                </TableCell>


                <TableCell align="right">

                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                  >

                    <Tooltip title="View details">

                      <IconButton
                        size="small"
                        onClick={() =>
                          handleOpenDetail(job, isPending)
                        }
                      >
                        <RemoveRedEye />
                      </IconButton>

                    </Tooltip>


                    {isPending &&
                      String(job.status).toLowerCase() ===
                      "pending" && (

                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            handleClaimJob(job)
                          }
                          disabled={actionLoading}
                        >
                          Claim
                        </Button>

                      )}

                  </Stack>

                </TableCell>

              </TableRow>

            ))}

          </TableBody>

        </Table>

      </TableContainer>
    );
  };

  // ===================================================
  // COPY FIELD VALUE
  // ===================================================

  const handleCopyValue = async (value, label) => {
    if (!value || value === "-") {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(value));

      showMessage(`${label} copied successfully.`, "success");
    } catch (error) {
      showMessage(`Unable to copy ${label}.`, "error");
    }
  };

  // ===================================================
  // INFO ROW WITH COPY ON HOVER
  // ===================================================

  const renderInfoRow = (label, value) => {
    const displayValue = value || "-";
    const hasValue = Boolean(value && value !== "-");

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",

          // Copy icon hidden by default
          "& .copy-field-button": {
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.2s ease, visibility 0.2s ease",
          },

          // Show copy icon when hovering this row
          "&:hover .copy-field-button": {
            opacity: hasValue ? 1 : 0,
            visibility: hasValue ? "visible" : "hidden",
          },
        }}
      >
        {/* LABEL */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>

        {/* VALUE + COPY ICON */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign="right"
            sx={{
              wordBreak: "break-word",
            }}
          >
            {displayValue}
          </Typography>

          {hasValue && (
            <Tooltip title={`Copy ${label}`}>
              <IconButton
                className="copy-field-button"
                size="small"
                onClick={() => handleCopyValue(value, label)}
                sx={{
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    );
  };

  // ===================================================
  // COPYABLE INFO ROW
  // Copy icon is visible ONLY when user hovers over row
  // ===================================================

  const renderCopyableInfoRow = (
    label,
    value
  ) => {
    const displayValue = value || "-";
    const hasValue = Boolean(value);

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .copy-field-button": {
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.2s ease, visibility 0.2s ease",
          },
          "&:hover .copy-field-button": {
            opacity: hasValue ? 1 : 0,
            visibility: hasValue ? "visible" : "hidden",
          },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          {label}
        </Typography>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            textAlign="right"
            sx={{
              wordBreak: "break-word",
            }}
          >
            {displayValue}
          </Typography>

          {hasValue && (
            <Tooltip title={`Copy ${label}`}>
              <IconButton
                className="copy-field-button"
                size="small"
                onClick={() =>
                  handleCopyValue(value, label)
                }
                sx={{
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    );
  };

  // ===================================================
  // RENDER
  // ===================================================

  return (

    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        backgroundColor: "#f7f8fa",
        minHeight: "100vh",
      }}
    >

      {/* =============================================
          HEADER
      ============================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          mb: 3,
        }}
      >

        <Box>

          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              letterSpacing: "-0.5px",
            }}
          >
            Certificate Job Queue
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
            }}
          >
            Manage, claim and submit certificate jobs assigned to your scope.
          </Typography>

        </Box>


        <Button
          variant="outlined"
          startIcon={
            refreshing
              ? <CircularProgress size={16} />
              : <Refresh />
          }
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>

      </Box>


      {/* =============================================
          SUMMARY CARDS
      ============================================== */}

      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              mr: 2,
              ml: 2
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Pending Jobs
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 1 }}
            >
              {pendingJobs.length}
            </Typography>

          </Paper>

        </Grid>


        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              mr: 2,
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              My Jobs
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 1 }}
            >
              {myJobs.length}
            </Typography>

          </Paper>

        </Grid>


        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              mr: 2,
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Assigned
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 1 }}
            >
              {
                myJobs.filter(
                  (job) =>
                    String(job.status).toLowerCase() ===
                    "assigned"
                ).length
              }
            </Typography>

          </Paper>

        </Grid>


        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              mr: 2,
            }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Submitted
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ mt: 1 }}
            >
              {
                myJobs.filter(
                  (job) =>
                    String(job.status).toLowerCase() ===
                    "submitted"
                ).length
              }
            </Typography>

          </Paper>

        </Grid>

      </Grid>


      {/* =============================================
          JOB QUEUE
      ============================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >

        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >

          <Tabs
            value={activeTab}
            onChange={(event, value) =>
              setActiveTab(value)
            }
          >

            <Tab label="Pending Queue" />

            <Tab label="My Assigned Jobs" />

          </Tabs>

        </Box>


        {/* PENDING */}

        {activeTab === 0 && (

          <Box>

            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >

              <Box>

                <Typography
                  variant="h6"
                  fontWeight={700}
                >
                  Available Certificate Jobs
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Jobs available according to your certificate scope.
                </Typography>

              </Box>

            </Box>

            {renderJobTable(
              pendingJobs,
              true
            )}

          </Box>

        )}


        {/* MY JOBS */}

        {activeTab === 1 && (

          <Box>

            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >

              <Box>

                <Typography
                  variant="h6"
                  fontWeight={700}
                >
                  My Assigned Jobs
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Jobs that have been assigned to you.
                </Typography>

              </Box>


              <FormControl
                size="small"
                sx={{
                  minWidth: 160,
                }}
              >

                <InputLabel>
                  Status
                </InputLabel>

                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value
                    )
                  }
                >

                  <MenuItem value="">
                    All
                  </MenuItem>

                  <MenuItem value="assigned">
                    Assigned
                  </MenuItem>

                  <MenuItem value="submitted">
                    Submitted
                  </MenuItem>

                  <MenuItem value="verified">
                    Verified
                  </MenuItem>

                  <MenuItem value="rejected">
                    Rejected
                  </MenuItem>

                  <MenuItem value="completed">
                    Completed
                  </MenuItem>

                </Select>

              </FormControl>

            </Box>

            {renderJobTable(
              myJobs,
              false
            )}

          </Box>

        )}

      </Paper>


      {/* =================================================
          DETAIL DIALOG
      ================================================== */}

      <Dialog
        open={detailDialog}
        onClose={() =>
          setDetailDialog(false)
        }
        maxWidth="lg"
        fullWidth
      >

        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >

            <Box>

              <Typography
                variant="h6"
                fontWeight={800}
              >
                {detailSource === "pending"
                  ? "Pending Certificate Job Details"
                  : "Certificate Job Details"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Job{" "}
                {detailSource === "pending"
                  ? selectedJob?.id || "-"
                  : selectedJob?.job?.id || "-"}
              </Typography>

            </Box>


            <IconButton
              onClick={() =>
                setDetailDialog(false)
              }
            >
              <Close />
            </IconButton>

          </Stack>

        </DialogTitle>


        <DialogContent sx={{ p: 3 }}>

          {!selectedJob ? (

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 5,
              }}
            >
              <CircularProgress />
            </Box>

          ) : (

            <Stack spacing={3}>

              {/* JOB */}
              {detailSource === "pending" ? (
                <Stack spacing={3}>

                  {/* PENDING JOB INFORMATION */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                      >
                        Job Information
                      </Typography>

                      <Chip
                        label={selectedJob?.status || "-"}
                        color={getStatusColor(selectedJob?.status)}
                        size="small"
                      />
                    </Stack>

                    {renderInfoRow(
                      "Job ID",
                      selectedJob?.id
                    )}

                    {renderInfoRow(
                      "Device Tag",
                      selectedJob?.device_tag
                    )}

                    {renderInfoRow(
                      "Status",
                      selectedJob?.status
                    )}

                    {renderInfoRow(
                      "Created At",
                      formatDate(selectedJob?.created)
                    )}

                    {renderInfoRow(
                      "Assigned Agent",
                      selectedJob?.assigned_agent_name
                    )}

                    {renderInfoRow(
                      "Assigned At",
                      formatDate(selectedJob?.assigned_at)
                    )}
                    {/* 
      {renderInfoRow(
        "Submitted At",
        formatDate(selectedJob?.submitted_at)
      )}

      {renderInfoRow(
        "Reviewed At",
        formatDate(selectedJob?.reviewed_at)
      )} */}

                    {renderInfoRow(
                      "Protocol Format",
                      selectedJob?.protocol_format
                    )}
                  </Paper>



                  {/* FILES */}
                  {/* <Paper
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={800}
        mb={1}
      >
        Certificate Files
      </Typography>

      {selectedJob?.files?.length ? (
        <Stack spacing={1}>
          {selectedJob.files.map((file, index) => (
            <Box
              key={file?.id || index}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
              >
                {file?.label ||
                  file?.name ||
                  `Certificate ${index + 1}`}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {file?.file ||
                  file?.path ||
                  "-"}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Alert
          severity="info"
          icon={<InfoOutlined />}
        >
          No certificate files uploaded yet.
        </Alert>
      )}
    </Paper> */}

                </Stack>
              ) : (
                <Stack spacing={3}>

                  {/* KEEP YOUR EXISTING MY ASSIGNED JOB UI HERE */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >

                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                      >
                        Job Information
                      </Typography>

                      <Chip
                        label={
                          selectedJob?.job?.status ||
                          "-"
                        }
                        color={getStatusColor(
                          selectedJob?.job?.status
                        )}
                        size="small"
                      />

                    </Stack>


                    {renderInfoRow(
                      "Job ID",
                      selectedJob?.job?.id
                    )}

                    {renderInfoRow(
                      "Created At",
                      formatDate(
                        selectedJob?.job?.created
                      )
                    )}

                    {renderInfoRow(
                      "Assigned At",
                      formatDate(
                        selectedJob?.job?.assigned_at
                      )
                    )}

                    {renderInfoRow(
                      "Submitted At",
                      formatDate(
                        selectedJob?.job?.submitted_at
                      )
                    )}

                  </Paper>


                  {/* VEHICLE / DEVICE */}

                  <Grid
                    container
                    spacing={2}
                  >

                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: "100%",
                          mr: 2
                        }}
                      >

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          mb={1}
                        >
                          Vehicle
                        </Typography>

                        {renderInfoRow(
                          "Registration No.",
                          selectedJob?.vehicle?.reg_no
                        )}

                        {renderInfoRow(
                          "Chassis No.",
                          selectedJob?.vehicle?.chassis_no
                        )}

                        {renderInfoRow(
                          "Engine No.",
                          selectedJob?.vehicle?.engine_no
                        )}

                        {renderInfoRow(
                          "Make",
                          selectedJob?.vehicle?.make
                        )}

                        {renderInfoRow(
                          "Model",
                          selectedJob?.vehicle?.model
                        )}

                        {renderInfoRow(
                          "Status",
                          selectedJob?.vehicle?.status
                        )}

                      </Paper>

                    </Grid>


                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          height: "100%",
                          mr: 2
                        }}
                      >

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          mb={1}
                        >
                          Device
                        </Typography>

                        {renderInfoRow(
                          "Device ESN",
                          selectedJob?.device?.device_esn
                        )}

                        {renderInfoRow(
                          "IMEI",
                          selectedJob?.device?.imei
                        )}

                        {renderInfoRow(
                          "ICCID",
                          selectedJob?.device?.iccid
                        )}

                        {renderInfoRow(
                          "FOTA Vendor",
                          selectedJob?.device?.fota_vendor
                        )}

                        {renderInfoRow(
                          "FOTA Version",
                          selectedJob?.device?.fota_version
                        )}

                        {renderInfoRow(
                          "FOTA Status",
                          selectedJob?.device?.fota_status
                        )}

                      </Paper>

                    </Grid>

                  </Grid>


                  {/* OWNER / DEALER / MANUFACTURER */}

                  <Grid
                    container
                    spacing={2}
                  >

                    <Grid
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >

                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mr: 2 }}
                      >

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                        >
                          Owner
                        </Typography>

                        <Typography
                          sx={{ mt: 1 }}
                          fontWeight={600}
                        >
                          {renderCopyableInfoRow(
                            // selectedJob?.owner_name ||
                            selectedJob?.owner?.contacts?.[0]?.name
                          )}
                          {renderCopyableInfoRow(
                            "Mobile No.",
                            selectedJob?.owner_mobile
                          )}
                        </Typography>

                      </Paper>

                    </Grid>


                    <Grid
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >

                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mr: 2 }}
                      >

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                        >
                          Dealer
                        </Typography>

                        <Typography
                          sx={{ mt: 1 }}
                          fontWeight={600}
                        >
                          {renderCopyableInfoRow(selectedJob?.dealer?.company_name ||
                            "-")}
                        </Typography>

                      </Paper>

                    </Grid>


                    <Grid
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >

                      <Paper
                        variant="outlined"
                        sx={{ p: 2, mr: 2 }}
                      >

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                        >
                          Manufacturer
                        </Typography>

                        <Typography
                          sx={{ mt: 1 }}
                          fontWeight={600}
                        >
                          {renderCopyableInfoRow(selectedJob?.manufacturer?.company_name ||
                            "-")}
                        </Typography>

                      </Paper>

                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          mr: 2,
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                          mb={1}
                        >
                          Technician
                        </Typography>

                        {renderCopyableInfoRow(
                          selectedJob?.technician_name
                        )}

                        {renderCopyableInfoRow(
                          "Mobile No.",
                          selectedJob?.technician_mobile
                        )}
                      </Paper>
                    </Grid>


                  </Grid>


                  {/* DRIVER CONTACTS */}

                  <Paper
                    variant="outlined"
                    sx={{ p: 2 }}
                  >

                    <Typography
                      variant="subtitle1"
                      fontWeight={800}
                      mb={2}
                    >
                      Driver Contacts
                    </Typography>

                    {selectedJob?.driver_contacts?.length ? (

                      <Stack spacing={1}>

                        {selectedJob.driver_contacts.map(
                          (driver) => (

                            <Box
                              key={driver.id}
                              sx={{
                                display: "flex",
                                justifyContent:
                                  "space-between",
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor:
                                  "action.hover",
                              }}
                            >

                              <Typography
                                fontWeight={600}
                              >
                                {renderCopyableInfoRow(driver.name)}
                              </Typography>

                              <Typography
                                color="text.secondary"
                              >
                                {renderCopyableInfoRow(driver.phone_no)}
                              </Typography>

                            </Box>

                          )
                        )}

                      </Stack>

                    ) : (

                      <Typography
                        color="text.secondary"
                      >
                        No driver contacts available.
                      </Typography>

                    )}

                  </Paper>



                  {/* CERTIFICATE FILES */}
                  <Paper
                    variant="outlined"
                    sx={{ p: 2 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                        >
                          Certificate Files
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {jobFiles.length + pendingFiles.length} file(s)
                        </Typography>
                      </Box>

                      {String(selectedJob?.job?.status).toLowerCase() === "assigned" && (
                        <Button
                          variant="contained"
                          startIcon={<CloudUpload />}
                          onClick={() => {
                            const protocol = getProtocolFormat();
                            const totalFiles = getTotalCertificateFiles();

                            if (
                              protocol === "nicrajasthan" &&
                              totalFiles >= 2
                            ) {
                              showMessage(
                                "NIC Rajasthan allows a maximum of 2 certificate files.",
                                "warning"
                              );
                              return;
                            }

                            setUploadDialog(true);
                          }}
                          disabled={
                            getProtocolFormat() === "nicrajasthan" &&
                            getTotalCertificateFiles() >= 2
                          }
                        >
                          Upload Certificate
                        </Button>
                      )}
                    </Stack>

                    {/* EXISTING + NEW CERTIFICATE FILES */}
                    {(jobFiles.length > 0 || pendingFiles.length > 0) ? (
                      <Stack spacing={1}>

                        {/* Already uploaded files */}
                        {jobFiles.map((file, index) => (
                          <Box
                            key={file.id || `uploaded-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <UploadFile color="primary" />

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  noWrap
                                >
                                  {file.label ||
                                    file.name ||
                                    `Certificate ${index + 1}`}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {file.file || file.path || "-"}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        ))}

                        {/* Newly selected files - not submitted yet */}
                        {pendingFiles.map((pendingFile, index) => (
                          <Box
                            key={pendingFile.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              border: "1px dashed",
                              borderColor: "primary.main",
                              borderRadius: 1,
                              backgroundColor: "action.hover",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ minWidth: 0 }}
                            >
                              <UploadFile color="primary" />

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  noWrap
                                >
                                  {pendingFile.label ||
                                    pendingFile.file?.name ||
                                    `Certificate ${index + 1}`}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {pendingFile.file?.name}
                                </Typography>
                              </Box>
                            </Stack>

                            {/* Remove only before submission */}
                            <Tooltip title="Remove file">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleRemovePendingFile(pendingFile.id)
                                }
                                disabled={actionLoading}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}

                      </Stack>
                    ) : (
                      <Alert
                        severity="info"
                        icon={<InfoOutlined />}
                      >
                        No certificate files uploaded yet.
                      </Alert>
                    )}
                  </Paper>


                  {/* TRACKING */}

                  <Paper
                    variant="outlined"
                    sx={{ p: 2 }}
                  >

                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      justifyContent="space-between"
                      gap={2}
                    >

                      <Box>

                        <Typography
                          variant="subtitle1"
                          fontWeight={800}
                        >
                          Vehicle Tracking
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Get the latest GPS packet for this vehicle.
                        </Typography>

                      </Box>


                      <Button
                        variant="outlined"
                        startIcon={
                          trackingLoading
                            ? <CircularProgress size={16} />
                            : <LocationOn />
                        }
                        onClick={handleTracking}
                        disabled={trackingLoading}
                      >
                        Check Location
                      </Button>

                    </Stack>


                    {tracking && (

                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor:
                            "action.hover",
                          borderRadius: 2,
                        }}
                      >

                        <Grid
                          container
                          spacing={2}
                        >

                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Latitude
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {tracking.lat ?? "-"}
                            </Typography>

                          </Grid>


                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Longitude
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {tracking.lon ?? "-"}
                            </Typography>

                          </Grid>


                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Speed
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {tracking.speed ?? "-"}
                            </Typography>

                          </Grid>


                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Ignition
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {tracking.ignition_status ?? "-"}
                            </Typography>

                          </Grid>


                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Main Power
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {tracking.main_power_status ?? "-"}
                            </Typography>

                          </Grid>


                          <Grid
                            size={{
                              xs: 6,
                              md: 2,
                            }}
                          >

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Last Packet
                            </Typography>

                            <Typography
                              fontWeight={700}
                            >
                              {formatDate(
                                tracking.entry_time
                              )}
                            </Typography>

                          </Grid>

                        </Grid>

                      </Box>

                    )}

                  </Paper>


                  {/* SOS */}

                  {String(
                    selectedJob?.job?.status
                  ).toLowerCase() === "assigned" && (

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor:
                            "warning.main",
                        }}
                      >

                        <Stack
                          direction={{
                            xs: "column",
                            md: "row",
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: "flex-start",
                            md: "center",
                          }}
                          gap={2}
                        >

                          <Box>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >

                              <Sos color="warning" />

                              <Typography
                                variant="subtitle1"
                                fontWeight={800}
                              >
                                SOS Controls
                              </Typography>

                            </Stack>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Send and verify SOS commands for this assigned vehicle.
                            </Typography>

                          </Box>


                          <Stack
                            direction="row"
                            spacing={1}
                          >

                            <Button
                              color="warning"
                              variant="contained"
                              onClick={() => {
                                setSosAction("start");
                                setSosDialog(true);
                              }}
                            >
                              Start SOS
                            </Button>

                            <Button
                              color="error"
                              variant="outlined"
                              onClick={() => {
                                setSosAction("stop");
                                setSosDialog(true);
                              }}
                            >
                              Stop SOS
                            </Button>

                          </Stack>

                        </Stack>

                      </Paper>

                    )}
                  {/* =================================================
    DEVICE COMMANDS
================================================== */}

                  {String(
                    selectedJob?.job?.status
                  ).toLowerCase() === "assigned" && (

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: "primary.main",
                        }}
                      >

                        <Stack spacing={2}>

                          <Box>

                            <Typography
                              variant="subtitle1"
                              fontWeight={800}
                            >
                              Device Commands
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              Send activation and reboot commands to this
                              assigned vehicle.
                            </Typography>

                          </Box>


                          {/* ACTIVATION COMMAND */}

                          <Box>

                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ mb: 1 }}
                            >
                              Activation Command
                            </Typography>

                            <Stack
                              direction={{
                                xs: "column",
                                sm: "row",
                              }}
                              spacing={1}
                            >

                              <Box
                                component="input"
                                value={activationCode}
                                onChange={(event) =>
                                  setActivationCode(event.target.value)
                                }
                                placeholder="Enter activation code (Example ACTV12345)"
                                disabled={activationLoading}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  height: 40,
                                  px: 1.5,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  fontSize: "14px",
                                  outline: "none",
                                  "&:focus": {
                                    borderColor: "primary.main",
                                  },
                                }}
                              />

                              <Button
                                variant="contained"
                                color="primary"
                                onClick={handleActivationCommand}
                                disabled={
                                  activationLoading ||
                                  !activationCode.trim()
                                }
                                startIcon={
                                  activationLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Security />
                                  )
                                }
                              >
                                {activationLoading
                                  ? "Activating..."
                                  : "Activate"}
                              </Button>

                            </Stack>

                          </Box>


                          <Divider />


                          {/* REBOOT COMMAND */}

                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}
                            justifyContent="space-between"
                            alignItems={{
                              xs: "flex-start",
                              sm: "center",
                            }}
                            spacing={2}
                          >

                            <Box>

                              <Typography
                                variant="body2"
                                fontWeight={700}
                              >
                                Reboot Device
                              </Typography>

                              {/* <Typography
            variant="caption"
            color="text.secondary"
          >
            Send the @REBOOT* command to the device.
          </Typography> */}

                            </Box>


                            <Button
                              variant="outlined"
                              color="error"
                              onClick={handleRebootCommand}
                              disabled={rebootLoading}
                              startIcon={
                                rebootLoading ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Refresh />
                                )
                              }
                            >
                              {rebootLoading
                                ? "Rebooting..."
                                : "Reboot Device"}
                            </Button>

                          </Stack>

                        </Stack>

                      </Paper>
                    )}
                </Stack>
              )}


            </Stack>

          )}

        </DialogContent>


        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >

          <Button
            onClick={() =>
              setDetailDialog(false)
            }
          >
            Close
          </Button>


          {String(
            selectedJob?.job?.status
          ).toLowerCase() === "assigned" && (

              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={handleSubmitJob}
                disabled={
                  actionLoading ||
                  (!jobFiles.length && !pendingFiles.length)
                }
              >
                Submit for Review
              </Button>

            )}

        </DialogActions>

      </Dialog>


      {/* =================================================
          UPLOAD DIALOG
      ================================================== */}

      <Dialog
        open={uploadDialog}
        onClose={() =>
          setUploadDialog(false)
        }
        maxWidth="sm"
        fullWidth
      >

        <DialogTitle>
          Upload Certificate
        </DialogTitle>

        <DialogContent>

          <Stack
            spacing={2}
            sx={{ pt: 1 }}
          >

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Upload a PDF, PNG, JPG, XLS or XLSX certificate.
              Maximum file size is 1 MB.
            </Typography>


            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFile />}
              sx={{
                py: 2,
              }}
            >

              {selectedFile
                ? selectedFile.name
                : "Choose Certificate File"}

              <input
                hidden
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                onChange={(event) => {

                  const file =
                    event.target.files?.[0];

                  setSelectedFile(
                    file || null
                  );

                }}
              />

            </Button>


            <Box>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Certificate Label
              </Typography>

              <input
                value={certificateLabel}
                onChange={(event) =>
                  setCertificateLabel(
                    event.target.value
                  )
                }
                placeholder="Example: RajasthanNIC cert"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "12px",
                  marginTop: "6px",
                  border: "1px solid #c4c4c4",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />

            </Box>

          </Stack>

        </DialogContent>


        <DialogActions>

          <Button
            onClick={() =>
              setUploadDialog(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              actionLoading
                ? <CircularProgress size={16} />
                : <CloudUpload />
            }
            onClick={handleUploadCertificate}
            disabled={actionLoading}
          >
            Upload
          </Button>

        </DialogActions>

      </Dialog>


      {/* =================================================
          SOS DIALOG
      ================================================== */}

      <Dialog
        open={sosDialog}
        onClose={() =>
          setSosDialog(false)
        }
        maxWidth="xs"
        fullWidth
      >

        <DialogTitle>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >

            <Warning color="warning" />

            <Typography
              fontWeight={800}
            >
              SOS Confirmation
            </Typography>

          </Stack>

        </DialogTitle>


        <DialogContent>

          <Typography>
            {sosAction === "start"
              ? "Are you sure you want to send the SOS START command to this vehicle?"
              : "Are you sure you want to send the SOS STOP command to this vehicle?"}
          </Typography>

        </DialogContent>


        <DialogActions>

          <Button
            onClick={() =>
              setSosDialog(false)
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color={
              sosAction === "start"
                ? "warning"
                : "error"
            }
            onClick={handleSOSAction}
            disabled={actionLoading}
          >
            {actionLoading
              ? "Processing..."
              : "Confirm"}
          </Button>

        </DialogActions>

      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>

      </Snackbar>

    </Box>
  );
};


export default CertificateJobQueue;